<template>
  <section :class="containerClasses">
    <div class="lead__inner">
      <div class="lead__image">
        <cdr-img
          :alt="image?.alt"
          :src="image?.src"
          class="lead__graphic"
        />
      </div>
      <div class="lead__content">
        <h1 class="lead__heading">
          <span v-dompurify-html="headingText" />
          <div class="lead__stamp">
            <cdr-img
              :alt="stamp?.alt"
              :src="stamp?.src"
            />
          </div>
        </h1>
        <span
          v-if="!isDoubleRewardsActive"
          v-dompurify-html="description"
          class="lead__subheading"
        />
      </div>
      <cdr-list class="lead__value-prop-list">
        <li
          v-for="prop in valueProps"
          :key="prop.key"
          class="lead__value-prop"
        >
          <cdr-img
            :alt="prop.image?.alt"
            :aria-hidden="!prop.image?.alt"
            :src="prop.image?.src"
          />
          <div class="lead__value-prop-text">
            <span v-dompurify-html="prop.description + `<sup>${prop.footnote}</sup>`" />
            <div
              v-if="prop.badge"
              v-dompurify-html="prop.badge"
              class="lead__value-prop-pill"
            />
          </div>
        </li>
      </cdr-list>
    </div>
  </section>
</template>

<script setup>
import { CdrImg, CdrList } from '@rei/cedar';
import { computed } from 'vue';
import convertToHtml from '@rei/rich-text-renderer';

const props = defineProps({
  description: {
    type: String,
    required: true,
  },
  heading: {
    type: Object,
    required: true,
  },
  image: {
    type: Object,
    required: true,
  },
  isDoubleRewardsActive: {
    type: Boolean,
    required: false,
    default: false,
  },
  stamp: {
    type: Object,
    required: true,
  },
  valueProps: {
    type: Array,
    required: true,
  },
});

const containerClasses = computed(() => ({
  lead: true,
  'lead--double-rewards': props.isDoubleRewardsActive,
}));

const headingText = computed(() => {
  const unwrapParagraphTag = {
    p: (attrs, child) => `${child}`,
  };

  return convertToHtml(props.heading, unwrapParagraphTag);
});
</script>

<style lang="scss">

.lead {
  background-color: $cdr-color-background-secondary;
}

.lead__inner {
  @include cdr-container;
  overflow: hidden;
}

.lead__image {
  position: relative;
  height: 40vw;
  min-height: 145px;
  max-height: 220px;
  max-width: 520px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin: 0 auto $cdr-space-one-and-a-half-x;
  @include cdr-md-mq-up {
    max-height: 22rem;
    max-width: 52rem;
  }
}

.lead__graphic {
  @include cdr-md-mq-up {
    height: initial;
  }
}

.lead__graphic {
  position: relative;
  z-index: 1;
}

.lead__content {
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-template-areas:
   "heading     stamp"
   "subheading  stamp";
  grid-template-columns: 1fr 10.4rem;
  grid-row-gap: $cdr-space-half-x;
  margin-bottom: $cdr-space-one-x;

  @include cdr-md-mq-up {
    grid-template-columns: 1fr;
    grid-template-areas:
      "heading"
      "subheading";
    grid-row-gap: 0;
    text-align: center;
  }
}

.lead__heading {
  @include cdr-text-heading-display-800;
  margin: 0;
  color: $cdr-color-text-brand;
  grid-area: heading;
  @include cdr-md-mq-up {
    @include cdr-text-heading-display-900;
    display: flex;
    justify-content: center;
    margin-top: $cdr-space-one-x;

    .lead__stamp {
      transform: translate(0, -$cdr-space-one-x);
    }
  }

  sup {
    font-weight: 700;
    font-size: 1.8rem;
    line-height: 0;
  }
}

.lead__subheading {
  @include cdr-text-body-300;
  grid-area: subheading;
  sup {
    // there is no token for a font this microscopic
    font-size: 0.8rem;
  }
}

.lead__stamp {
  position: absolute;
  grid-area: stamp;
  display: flex;
  align-items: center;
  transform: translate(0, -50%) rotate(20deg);
  margin-left: $cdr-space-one-x;
  max-width: 8.8rem;
  top: 50%;
  right: 0;

  @include cdr-md-mq-up {
    position: relative;
    top: 0;
    transform: scale(1.45) translate(10px, -10px);
  }
}

.lead__value-prop-list.lead__value-prop-list {
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: $cdr-space-one-x;
  padding-bottom: $cdr-space-two-x;
  text-align: center;

  img {
    max-height: 46px;
    display: block;
    margin-bottom: $cdr-space-half-x;
  }

  span {
    @include cdr-text-utility-sans-100;
  }
}

.lead__value-prop {
  @include cdr-text-utility-sans-100;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0!important;
  position: relative;

  sup {
    font-size: 0.6rem;
  }

  &:nth-child(-n + 2) {
    &::after {
      position: absolute;
      content: '';
      display: block;
      width: 1px;
      height: 100%;
      background-color: $cdr-color-border-primary;
      top: 0;
      right: -$cdr-space-half-x;
      bottom: 0;
    }
  }
}

.lead__value-prop-pill {
  align-self: baseline;
  @include pill;
  font-size: 0.8rem;
  margin-top: $cdr-space-half-x;
  @include cdr-sm-mq-up {
    font-size: 1.2rem;
  }
  @include cdr-md-mq-up {
    font-size: 0.9rem;
  }
  @include cdr-lg-mq-up {
    font-size: 1.2rem;
  }
}

.lead--double-rewards {
  .lead__inner {
    padding: $cdr-space-one-x $cdr-space-one-x 0;

    @include cdr-md-mq-up {
      padding: $cdr-space-two-x $cdr-space-one-x 0;
    }
  }

  .lead__image {
    height: initial;
    max-height: initial;
    min-height: initial;
    max-width: initial;
    margin-bottom: $cdr-space-one-x;

    @include cdr-xs-mq-up {
      max-width: 44.3rem;
    }

    @include cdr-md-mq-up {
      max-width: initial;
      margin-bottom: $cdr-space-one-and-a-half-x;
    }
  }

  .lead__graphic {
    width: initial;
    max-width: 100%;
  }

  .lead__stamp {
    display: none;
  }
  .lead__content {
    display: block;
    margin-bottom: $cdr-space-one-x;

    @include cdr-md-mq-up {
      margin-bottom: $cdr-space-one-and-a-half-x;
    }
  }

  .lead__heading {
    font-size: 3.2rem;
    line-height: 3.6rem;
    text-wrap: initial;

    @include cdr-md-mq-up {
      @include cdr-text-heading-display-1000;
    }
  }

  .lead__value-prop-list {
    display: block;
    text-align: left;
    padding-bottom: $cdr-space-one-and-a-half-x;

    @include cdr-sm-mq-up {
      display: grid;
      text-align: center;
    }

    @include cdr-md-mq-only {
      padding-bottom: $cdr-space-half-x;
    }

    @include cdr-lg-mq-only {
      padding-bottom: $cdr-space-one-x;
    }
  }

  .lead__value-prop {
    @include cdr-sm-mq-down {
      display: grid;
      grid-template-columns: auto 1fr;
      grid-auto-flow: column;
      align-content: center;
      margin-bottom: $cdr-space-one-and-a-half-x;

      &:last-child {
        margin-bottom: 0;
      }

      &:after {
        display: none;
      }

      span {
        padding-right: $cdr-space-one-and-a-half-x;
        font-size: 1.6rem;
        line-height: 2.2rem;
      }
    }

    @include cdr-md-mq-down {
      span {
        display: block;
      }
    }
  }

  .lead__value-prop img {
    @include cdr-sm-mq-down {
      width: 9rem;
      height: auto;
      max-height: auto;
      align-self: center;
      margin-right: $cdr-space-one-x;
      margin-bottom: 0;
    }
  }

  .lead__value-prop-pill {
    display: inline-block;
    font-size: 1.2rem;
    line-height: 1.8rem;
    font-weight: 500;
    padding: 0.3rem 1.2rem;
    background-color: $cdr-membership-vibrant-color-text-primary;

    @include cdr-lg-mq-only {
      font-size: 1rem;
      line-height: 1.6rem;
    }
  }
}
</style>
